import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

const commonButtonSx: SxProps<Theme> = {
  gap: 0.75,
  height: 40,
  px: 1,
  textTransform: 'none',
  color: 'text.primary',
  '& .MuiButton-startIcon': {
    m: 0,
    '& svg': { width: 18, height: 18 },
  },
};

const inactiveButtonSx: SxProps<Theme> = {
  ...commonButtonSx,
  fontWeight: 'fontWeightRegular',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  '& .MuiButton-startIcon': {
    ...((commonButtonSx as Record<string, unknown>)['& .MuiButton-startIcon'] as object),
    color: 'text.secondary',
  },
  '&:hover': {
    borderColor: 'divider',
    bgcolor: 'background.paper',
  },
};

const activeButtonSx: SxProps<Theme> = {
  ...commonButtonSx,
  fontWeight: 'fontWeightBold',
  borderColor: 'primary.main',
  bgcolor: 'primary.light',
  '& .MuiButton-startIcon': {
    ...((commonButtonSx as Record<string, unknown>)['& .MuiButton-startIcon'] as object),
    color: 'text.primary',
  },
  '&:hover': {
    borderColor: 'primary.main',
    bgcolor: 'primary.light',
  },
};

const badgeSx: SxProps<Theme> = {
  px: 0.5,
  height: 16,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 1,
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  fontSize: 10,
  fontWeight: 'fontWeightBold',
  lineHeight: 1,
};

export interface MoreFiltersButtonProps {
  onClick: () => void;
  activeFiltersCount?: number;
}

export const MoreFiltersButton = ({ onClick, activeFiltersCount = 0 }: MoreFiltersButtonProps) => {
  const { t } = useTranslation();
  const hasActiveFilters = activeFiltersCount > 0;

  return (
    <Button
      variant="outlined"
      startIcon={hasActiveFilters ? <FilterAltIcon /> : <FilterAltOutlinedIcon />}
      onClick={onClick}
      data-test="more-filters-button"
      sx={hasActiveFilters ? activeButtonSx : inactiveButtonSx}
    >
      {t('More Filters')}
      {hasActiveFilters && (
        <Box component="span" data-test="more-filters-count" sx={badgeSx}>
          {activeFiltersCount}
        </Box>
      )}
    </Button>
  );
};
